import { MouseEvent as ReactMouseEvent, ReactNode, useState } from 'react';
import styled from 'styled-components';

import { LegacyButton as Button } from 'components/Button/LegacyButton';
import { colors, transition } from 'theme/theme';
import { ClickOutside } from 'utils/ClickOutside';

import {
  LegacyDropdownContainer,
  LegacyDropdownItemsContainer,
  LegacyDropdownMenuContainer,
  LegacyDropdownMenuList,
} from './LegacyDropdown.styled';
import { LegacyDropdownPlacement } from './LegacyDropdownTypes';
import { LegacyTrigger } from './LegacyTrigger';

type Props = {
  title: (open: boolean, tabIndex?: number) => ReactNode;
  placement?: LegacyDropdownPlacement;
  mobilePlacement?: LegacyDropdownPlacement;
  menuMarginLeft?: number;
  hideCloseTrigger?: boolean;
  unstyled?: boolean;
  render: (arg0: { open: boolean; closeMenu: () => void }) => ReactNode;
  'data-qa-id': string;
  'aria-label'?: string;
  onClick?: (e: ReactMouseEvent, open: boolean) => void;
  onClose?: () => void;
  noTriggerPadding?: boolean;
  tabIndex?: number;
};

/**
 * @deprecated Use the composable `<Dropdown>` instead
 */
export function LegacyDropdown({
  title,
  placement = 'down',
  mobilePlacement = 'down',
  menuMarginLeft,
  hideCloseTrigger,
  unstyled,
  render,
  'data-qa-id': qaId,
  'aria-label': ariaLabel,
  onClick,
  onClose,
  noTriggerPadding,
  tabIndex,
}: Props) {
  const [open, setOpen] = useState(false);

  const toggleMenu = (e: ReactMouseEvent) => {
    if (open && onClose) {
      onClose();
    }

    setOpen(!open);
    e.preventDefault();
  };

  const closeMenu = (e?: MouseEvent | TouchEvent) => {
    if (open && onClose) {
      onClose();
    }

    if (open) {
      if (e) {
        e.stopPropagation();
      }
      setOpen(false);
    }
  };

  const onTriggerClick = (e: ReactMouseEvent) => {
    toggleMenu(e);

    if (onClick) {
      onClick(e, !open);
    }
  };

  return (
    <LegacyDropdownContainer>
      <LegacyTrigger
        onTriggerClick={onTriggerClick}
        qaId={qaId}
        ariaLabel={ariaLabel}
        title={title(open, tabIndex)}
        noPadding={noTriggerPadding}
      />
      <LegacyDropdownMenuContainer
        $open={open}
        $desktopPlacement={placement}
        $mobilePlacement={mobilePlacement}
        $marginLeft={menuMarginLeft}
        data-qa-id={qaId}
      >
        <ClickOutside onClickOutside={closeMenu}>
          <LegacyDropdownMenuList
            $desktopPlacement={placement}
            $mobilePlacement={mobilePlacement}
          >
            {!hideCloseTrigger && (
              <LegacyTrigger
                onTriggerClick={onTriggerClick}
                qaId={qaId}
                ariaLabel={ariaLabel}
                title={title(open)}
                noPadding={noTriggerPadding}
              />
            )}
            <LegacyDropdownItemsContainer
              $unstyled={unstyled}
              $desktopPlacement={placement}
              $mobilePlacement={mobilePlacement}
            >
              {render({
                closeMenu,
                open,
              })}
            </LegacyDropdownItemsContainer>
          </LegacyDropdownMenuList>
        </ClickOutside>
      </LegacyDropdownMenuContainer>
    </LegacyDropdownContainer>
  );
}

export const DropdownItem = styled(Button)`
  color: ${colors.lightContentGrey};
  transition: all ${transition};

  &:hover {
    color: ${colors.brandBlue};
  }

  display: flex;
  align-items: center;
  padding: 5px 10px 8px;
  font-size: 14px;
  min-width: 112px;
`;
